// composables/usePrograms.ts
import { ref } from 'vue';
import axios from 'axios';
import type School from '@/models/school';
import type Program from '@/models/program';

export function usePrograms() {
  const programs = ref<Program[]>([]);
  const isLoading = ref(false);
  const error = ref(false);
  const errorMsg = ref('');

  const parseActiveProgramsJson = (raw: any): Program[] => {
    const programs: Program[] = [];
    const list = Array.isArray(raw) ? raw : [raw];

    const hash = (s: string) => {
      let h = 0;
      for (let i = 0; i < s.length; i++) h = Math.imul(31, h) + s.charCodeAt(i) | 0;
      return Math.abs(h);
    };

    const DEGREE_TOKENS = [
      "BSE", "BSN", "BAS", "BFA", "BBA", "BEng", "BTech", "BPhil",
      "BS", "BA"
    ];

    const extractDegreeAbbr = (codeToken: string): string | null => {
      for (const t of DEGREE_TOKENS) {
        if (new RegExp(`\\b${t}\\b`).test(codeToken)) return t;
        if (codeToken.includes(t)) return t;
      }
      return null;
    };

    const enabledPrograms = [
      { name: 'Accounting', degree: 'BS', exact: true }
      // { name: 'Biology', degree: 'BS', exact: true },
      // { name: 'Biochemistry', degree: 'BA', exact: true }
    ];

    const isProgramEnabled = (programName: string, degreeType: string | null): boolean => {
      return enabledPrograms.some(ep => {
        const nameMatch = ep.exact
          ? programName.toLowerCase() === ep.name.toLowerCase()
          : programName.toLowerCase().includes(ep.name.toLowerCase());
        const degreeMatch = !ep.degree || degreeType === ep.degree;
        return nameMatch && degreeMatch;
      });
    };

    list.forEach((collegeObj: any) => {
      const collegeName = collegeObj?.College ?? "Unknown College";
      (collegeObj?.Departments ?? []).forEach((dept: any) => {
        (dept?.Programs ?? []).forEach((p: string) => {
          const m = p.match(/^(.*)\s+\(([^)]+)\)$/);
          const name = (m?.[1] ?? p).trim();
          const codeToken = (m?.[2] ?? p).trim();

          const degree_abbr = extractDegreeAbbr(codeToken);
          const display_name = degree_abbr ? `${name} (${degree_abbr})` : name;
          // const isEnabled = isProgramEnabled(name, degree_abbr);
          const isEnabled = true;

          programs.push({
            name,
            id: hash(`${collegeName}:${codeToken}`),
            code: codeToken,
            college: collegeName,
            periodDegreeJoins: [],
            plan_period_join: null as unknown as number,
            plan_terms: [],
            requirements: [],
            disable: !isEnabled,
            degree_abbr,
            display_name,
          } as unknown as Program);
        });
      });
    });

    programs.sort((a, b) =>
      String(a.display_name ?? a.name ?? "").localeCompare(String(b.display_name ?? b.name ?? ""))
    );
    return programs;
  };

  const parseNdjsonToArray = (ndjsonText: string): any[] => {
    const lines = ndjsonText.split(/\r?\n/);
    const out: any[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      try {
        out.push(JSON.parse(line));
      } catch (err) {
        console.error(`NDJSON parse error at line ${i + 1}:`, err, line);
        throw new Error(`Invalid NDJSON at line ${i + 1}`);
      }
    }
    return out;
  };

  const getProgramsFromLocalNdjson = async (
    school: School,
    path = "/data/ActivePrograms.json"
  ): Promise<Program[]> => {
    try {
      isLoading.value = true;
      const { data: text } = await axios.get<string>(path, {
        responseType: "text",
        transformResponse: (x) => x,
        headers: { "Cache-Control": "no-cache" }
      });

      const rawArray = parseNdjsonToArray(text);
      const parsedPrograms = parseActiveProgramsJson(rawArray);
      programs.value = parsedPrograms;
      return parsedPrograms;
    } catch (e) {
      console.error("Failed to load programs from local NDJSON:", e);
      error.value = true;
      errorMsg.value = "Could not load programs from local NDJSON file.";
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  const getProgramRequirement = async (program: Program): Promise<any | null> => {
    try {
      if (!program?.code) throw new Error("Program missing code.");
      if (!program?.college) throw new Error("Program missing college.");

      const base = (import.meta.env.VITE_PROGRAM_REQ_S3_BASE ||
        "https://program-json-bucket.s3.us-west-2.amazonaws.com/Loaded").replace(/\/+$/, "");
      const term = (import.meta.env.VITE_PROGRAM_REQ_TERM || "2254").toString();

      const normalizeCollege = (s: string) => s.replace(/\s+/g, " ").trim();
      const college = normalizeCollege(program.college);

      const url =
        `${base}/TERM=${encodeURIComponent(term)}` +
        `/COLLEGE=${encodeURIComponent(college)}` +
        `/PROGRAM_CODE=${encodeURIComponent(program.code)}/degs.json`;

      console.log("[S3] GET", url);
      const { data } = await axios.get(url, { headers: { "Cache-Control": "no-cache" } });

      let reqObj: any = null;

      if (data && typeof data.degreq_json === "string") {
        try {
          reqObj = JSON.parse(data.degreq_json);
        } catch {
          try {
            reqObj = JSON.parse(JSON.parse(data.degreq_json));
          } catch (parseError) {
            console.error('Failed to parse degreq_json:', parseError);
            throw new Error("Failed to parse degree requirements JSON.");
          }
        }
      } else if (data && typeof data === "object") {
        reqObj = data;
      } else {
        throw new Error("Unexpected degs.json format.");
      }

      if (!reqObj || typeof reqObj !== 'object') {
        throw new Error("Invalid requirements object after parsing.");
      }

      return reqObj;
    } catch (err: any) {
      console.error("getProgramRequirement failed:", err);
      const status = err?.response?.status;
      error.value = true;
      errorMsg.value =
        status === 403 ? "Access denied (403) fetching degree requirements from S3."
          : status === 404 ? "Degree requirements file not found in S3."
            : `Failed to fetch degree requirements: ${err.message || 'Unknown error'}`;
      return null;
    }
  };

  return {
    programs,
    isLoading,
    error,
    errorMsg,
    getProgramsFromLocalNdjson,
    getProgramRequirement,
  };
}